import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, Button, Badge, Form, Row, Col, Modal, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Ticket, PurchaseRequest, Workflow, Group, WorkflowCustomField, User, TicketTemplate } from '../types';
import { API_URL } from '../config';
import './Tickets.css';
import './Dashboard.css';
import useAlert from '../hooks/useAlert';
import { useArea } from '../context/AreaContext';
import { useAuth } from '../context/AuthContext';
import DataTable, { Column } from '../components/DataTable';
import FilterPanel, {
  ActiveFilter,
  AssigneePeerOption,
  FilterAssigneeCombobox,
  FilterControl,
  FilterDropdownSelect,
  FilterField,
  FilterInputGroup,
  FilterSection,
  FilterToggle,
} from '../components/filters/FilterPanel';
import StatusMultiFilter from '../components/filters/StatusMultiFilter';
import {
  parseStatusesFromSearchParams,
  restoreDashboardStatuses,
  serializeDashboardParams,
  statusesForArea,
} from '../utils/dashboardStatusFilter';
import CustomTableField from '../components/CustomTableField';
import { createDefaultTableValue, hasTableContent, serializeTableValue } from '../utils/tableCustomField';
import { shouldShowConditionalField } from '../utils/conditionalField';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../utils/currencyInput';
import { searchActiveUsers } from '../utils/searchUsers';
import { URGENCY_OPTIONS, getUrgencyColor, getUrgencyLabel, type TicketUrgency } from '../utils/urgency';
import { getPriorityColor, getPriorityLabel } from '../utils/priority';
import { canManageTicketPriority } from '../utils/roles';
import {
  createTicketTemplate,
  deleteTicketTemplate,
  listTicketTemplateCategories,
  listTicketTemplates,
} from '../services/ticketTemplates';
import {
  ATTACHMENT_ACCEPT,
  validateAttachmentFile,
  validateAttachmentFiles,
} from '../utils/uploadValidation';
import { getPurchaseDisplayId } from '../utils/purchaseDisplayId';
import { getAuthToken } from '../utils/authToken';
import { upsertCustomFieldValue } from '../utils/purchaseCustomFieldUpsert';
import {
  CLOSED_VISIBILITY_OPTIONS,
  ClosedVisibility,
  DEFAULT_CLOSED_VISIBILITY,
  closedVisibilityChipLabel,
  closedVisibilityNotice,
  normalizeClosedVisibility,
} from '../utils/closedItemsVisibility';
const ASSIGNED_PEER_SESSION_PREFIX = 'dashboard_filter_assigned_peer_id';
const TICKETS_ITEMS_PER_PAGE_STORAGE_KEY = 'tickets_items_per_page';
const TICKETS_FILTERS_SESSION_PREFIX = 'tickets_filters';
const ALLOWED_ITEMS_PER_PAGE = [5, 10, 20, 50, 100] as const;
type IncidentListItem = Ticket | PurchaseRequest;

function assignedPeerSessionKey(userId: number, area: string) {
  return `${ASSIGNED_PEER_SESSION_PREFIX}:${userId}:${area}`;
}

function ticketsFiltersSessionKey(area: string) {
  return `${TICKETS_FILTERS_SESSION_PREFIX}:${area}`;
}

function getInitialItemsPerPage(): number {
  if (typeof window === 'undefined') return 10;
  const raw = window.localStorage.getItem(TICKETS_ITEMS_PER_PAGE_STORAGE_KEY);
  if (!raw) return 10;
  const parsed = Number(raw);
  return ALLOWED_ITEMS_PER_PAGE.includes(parsed as typeof ALLOWED_ITEMS_PER_PAGE[number]) ? parsed : 10;
}

const Tickets: React.FC = () => {
  const [tickets, setTickets] = useState<IncidentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterUrgency, setFilterUrgency] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterAssignedToPeerId, setFilterAssignedToPeerId] = useState<number | null>(null);
  const [assignedPeerSearch, setAssignedPeerSearch] = useState('');
  const [filterCreatedFrom, setFilterCreatedFrom] = useState('');
  const [filterCreatedTo, setFilterCreatedTo] = useState('');
  const [closedVisibility, setClosedVisibility] = useState<ClosedVisibility>(DEFAULT_CLOSED_VISIBILITY);
  const [hiddenConcludedTotal, setHiddenConcludedTotal] = useState(0);
  const [includeThirdPartyTickets, setIncludeThirdPartyTickets] = useState<boolean>(true);
  const [showSubtasks, setShowSubtasks] = useState(false);
  const [filterCreatedByMe, setFilterCreatedByMe] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchInputText, setSearchInputText] = useState('');
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [myGroupPeers, setMyGroupPeers] = useState<AssigneePeerOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getInitialItemsPerPage);
  const [totalTickets, setTotalTickets] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert, AlertComponent } = useAlert();
  const { currentArea } = useArea();
  const { user, refreshUser } = useAuth();
  const canSeeTicketPriority = useMemo(() => canManageTicketPriority(user), [user]);

  useEffect(() => {
    setFiltersInitialized(false);
    const params = new URLSearchParams(location.search);
    const hasUrlFilters = [
      'status',
      'statuses',
      'priority',
      'urgency',
      'workflow_id',
      'assigned_group_id',
      'assigned_to',
      'created_from',
      'created_to',
      'search',
      'created_by_me',
      'closed_visibility',
    ].some((key) => params.has(key));

    let restored: any = null;
    if (!hasUrlFilters) {
      try {
        const raw = sessionStorage.getItem(ticketsFiltersSessionKey(currentArea));
        restored = raw ? JSON.parse(raw) : null;
      } catch {
        restored = null;
      }
    }

    if (hasUrlFilters) {
      setFilterStatuses(statusesForArea(currentArea, parseStatusesFromSearchParams(params)));
      setFilterPriority(params.get('priority') || '');
      setFilterUrgency(params.get('urgency') || '');
      setFilterWorkflow(params.get('workflow_id') || '');
      setFilterGroup(params.get('assigned_group_id') || '');
      const assignedToParam = params.get('assigned_to');
      if (assignedToParam) {
        const n = parseInt(assignedToParam, 10);
        setFilterAssignedToPeerId(Number.isNaN(n) ? null : n);
      } else {
        setFilterAssignedToPeerId(null);
      }
      setFilterCreatedFrom(params.get('created_from') || '');
      setFilterCreatedTo(params.get('created_to') || '');
      setClosedVisibility(normalizeClosedVisibility(params.get('closed_visibility')));
      const initialSearchText = params.get('search') || '';
      setSearchText(initialSearchText);
      setSearchInputText(initialSearchText);
      setShowSubtasks(false);
      setFilterCreatedByMe(params.get('created_by_me') === '1' || params.get('created_by_me') === 'true');
      setCurrentPage(1);
    } else {
      setFilterStatuses(statusesForArea(currentArea, restoreDashboardStatuses(restored)));
      setFilterPriority(restored?.filterPriority || '');
      setFilterUrgency(restored?.filterUrgency || '');
      setFilterWorkflow(restored?.filterWorkflow || '');
      setFilterGroup(restored?.filterGroup || '');
      setFilterAssignedToPeerId(
        typeof restored?.filterAssignedToPeerId === 'number' ? restored.filterAssignedToPeerId : null
      );
      setFilterCreatedFrom(restored?.filterCreatedFrom || '');
      setFilterCreatedTo(restored?.filterCreatedTo || '');
      setClosedVisibility(normalizeClosedVisibility(restored?.closedVisibility));
      setSearchText(restored?.searchText || '');
      setSearchInputText(restored?.searchText || '');
      setShowSubtasks(
        typeof restored?.showSubtasks === 'boolean' ? restored.showSubtasks : false
      );
      setFilterCreatedByMe(Boolean(restored?.filterCreatedByMe));
      setCurrentPage(1);
    }

    setFiltersInitialized(true);
  }, [location.search, currentArea]);

  useEffect(() => {
    if (!filtersInitialized) return;
    const payload = {
      filterStatuses,
      filterPriority,
      filterUrgency,
      filterWorkflow,
      filterGroup,
      filterAssignedToPeerId,
      filterCreatedFrom,
      filterCreatedTo,
      closedVisibility,
      searchText,
      showSubtasks,
      filterCreatedByMe,
    };
    sessionStorage.setItem(ticketsFiltersSessionKey(currentArea), JSON.stringify(payload));
  }, [
    filtersInitialized,
    currentArea,
    filterStatuses,
    filterPriority,
    filterUrgency,
    filterWorkflow,
    filterGroup,
    filterAssignedToPeerId,
    filterCreatedFrom,
    filterCreatedTo,
    closedVisibility,
    searchText,
    showSubtasks,
    filterCreatedByMe,
  ]);

  useEffect(() => {
    fetchWorkflows();
    fetchGroups();
    fetchMyGroupPeers();
  }, [currentArea]);

  useEffect(() => {
    if (!user) return;
    setIncludeThirdPartyTickets(user.include_third_party_tickets !== false);
  }, [user?.id, user?.include_third_party_tickets]);

  // Prioridade na API é interna (USUARIO não filtra nem vê coluna); alinhar UI e query ao backend.
  useEffect(() => {
    if (!filtersInitialized) return;
    if (!canSeeTicketPriority) {
      if (filterPriority) setFilterPriority('');
      if (sortColumn === 'priority') {
        setSortColumn('created_at');
        setSortDirection('desc');
      }
    }
  }, [filtersInitialized, canSeeTicketPriority, filterPriority, sortColumn]);

  // Resetar para página 1 quando filtros mudarem
  useEffect(() => {
    if (!filtersInitialized) return;
    setCurrentPage(1);
  }, [filterStatuses, filterUrgency, filterPriority, filterWorkflow, filterGroup, filterAssignedToPeerId, filterCreatedFrom, filterCreatedTo, closedVisibility, searchText, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, filtersInitialized]);

  const myGroupPeerOptions = myGroupPeers;

  const filteredPeerSuggestions = useMemo(() => {
    const q = assignedPeerSearch.trim().toLowerCase();
    const list = myGroupPeerOptions;
    if (!q) return list.slice(0, 20);
    return list
      .filter(
        (p) =>
          (p.user_name || '').toLowerCase().includes(q) ||
          (p.user_email || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [myGroupPeerOptions, assignedPeerSearch]);

  const workflowFilterOptions = useMemo(
    () => workflows.map((workflow) => ({ value: String(workflow.id), label: workflow.name })),
    [workflows],
  );

  const groupFilterOptions = useMemo(
    () =>
      groups
        .filter((g) => g.is_active)
        .map((group) => ({ value: String(group.id), label: group.name })),
    [groups],
  );

  const priorityFilterOptions = useMemo(
    () => [
      { value: 'p1', label: 'P1' },
      { value: 'p2', label: 'P2' },
      { value: 'p3', label: 'P3' },
      { value: 'p4', label: 'P4' },
    ],
    [],
  );

  const closedVisibilityFilterOptions = useMemo(
    () => CLOSED_VISIBILITY_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000 },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchMyGroupPeers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups/my-peers`);
      setMyGroupPeers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar colegas de grupo:', error);
      setMyGroupPeers([]);
    }
  };

  const fetchWorkflows = async () => {
    try {
      // Para criação de tickets, buscar apenas workflows publicados filtrados por área
      const areaType = currentArea === 'purchases' ? 'purchases' : 'tickets';
      const response = await axios.get(`${API_URL}/api/workflows/published`, {
        params: { area_type: areaType }
      });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const fetchTickets = useCallback(async () => {
    // Tela de incidentes: só carrega no contexto tickets (feed dashboard-items).
    if (currentArea !== 'tickets') {
      setTickets([]);
      setTotalTickets(0);
      setHiddenConcludedTotal(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const params: any = {
        skip,
        limit: itemsPerPage,
        // Alinha com o backend do feed (created_by) e com a Dashboard.
        sort_by: sortColumn === 'creator' ? 'created_by' : sortColumn,
        sort_order: sortDirection,
      };
      const selectedStatuses = statusesForArea('tickets', filterStatuses);
      if (selectedStatuses.length > 0) params.statuses = selectedStatuses;
      if (canSeeTicketPriority && filterPriority) params.priority = filterPriority;
      if (filterUrgency) params.urgency = filterUrgency;
      if (filterWorkflow) params.workflow_id = filterWorkflow;
      if (filterGroup) params.assigned_group_id = filterGroup;
      if (filterAssignedToPeerId != null) params.assigned_to = filterAssignedToPeerId;
      if (filterCreatedFrom) params.created_from = filterCreatedFrom;
      if (filterCreatedTo) params.created_to = filterCreatedTo;
      if (searchText) params.search = searchText;
      if (filterCreatedByMe) params.created_by_me = true;
      if (closedVisibility !== DEFAULT_CLOSED_VISIBILITY) params.closed_visibility = closedVisibility;
      params.include_third_party_tickets =
        user ? includeThirdPartyTickets : true;
      if (showSubtasks) {
        params.include_subtasks = true;
      } else {
        params.parent_ticket_id = 0;
        params.include_subtasks = false;
      }

      const requestConfig = {
        params,
        paramsSerializer: { serialize: serializeDashboardParams },
      };
      const ticketsResponse = await axios.get(`${API_URL}/api/tickets/dashboard-items`, requestConfig);

      const summaryParams = { ...params };
      delete summaryParams.skip;
      delete summaryParams.limit;
      delete summaryParams.sort_by;
      delete summaryParams.sort_order;
      const summaryResponse = await axios.get(`${API_URL}/api/tickets/dashboard-items-summary`, {
        params: summaryParams,
        paramsSerializer: { serialize: serializeDashboardParams },
      });
      setTotalTickets(summaryResponse.data?.total || 0);
      setHiddenConcludedTotal(summaryResponse.data?.hidden_concluded_total || 0);

      setTickets(ticketsResponse.data || []);
    } catch (error) {
      console.error('Erro ao carregar tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatuses, filterPriority, filterUrgency, filterWorkflow, filterGroup, filterAssignedToPeerId, filterCreatedFrom, filterCreatedTo, closedVisibility, searchText, includeThirdPartyTickets, showSubtasks, filterCreatedByMe, currentPage, itemsPerPage, sortColumn, sortDirection, canSeeTicketPriority, user, currentArea]);

  useEffect(() => {
    if (!filtersInitialized) return;
    fetchTickets();
  }, [fetchTickets, filtersInitialized]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TICKETS_ITEMS_PER_PAGE_STORAGE_KEY, String(itemsPerPage));
  }, [itemsPerPage]);

  useEffect(() => {
    if (filterAssignedToPeerId == null) return;
    const peer = myGroupPeerOptions.find((p) => p.user_id === filterAssignedToPeerId);
    if (peer) setAssignedPeerSearch(peer.user_name);
  }, [filterAssignedToPeerId, myGroupPeerOptions]);

  // Restaurar membro da sessão antes de persistir (evita apagar sessionStorage no primeiro paint)
  useEffect(() => {
    if (!user?.id || !filtersInitialized) return;
    const params = new URLSearchParams(location.search);
    if (params.get('assigned_to')) return;
    const peerRaw = sessionStorage.getItem(assignedPeerSessionKey(user.id, currentArea));
    if (peerRaw) {
      const pid = parseInt(peerRaw, 10);
      if (!Number.isNaN(pid)) setFilterAssignedToPeerId(pid);
    }
  }, [user?.id, filtersInitialized, currentArea, location.search]);

  useEffect(() => {
    if (!user?.id || !filtersInitialized) return;
    if (filterAssignedToPeerId != null) {
      sessionStorage.setItem(assignedPeerSessionKey(user.id, currentArea), String(filterAssignedToPeerId));
    }
  }, [filterAssignedToPeerId, user?.id, currentArea, filtersInitialized]);

  const selectAssignedPeer = (peer: AssigneePeerOption) => {
    setFilterAssignedToPeerId(peer.user_id);
    setAssignedPeerSearch(peer.user_name);
  };

  const clearAssignedPeer = () => {
    setFilterAssignedToPeerId(null);
    setAssignedPeerSearch('');
    if (user?.id) {
      sessionStorage.removeItem(assignedPeerSessionKey(user.id, currentArea));
    }
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'warning',
      aberto: 'warning',
      em_andamento: 'info',
      aguardando_informacao_usuario: 'secondary',
      aguardando_terceiros: 'secondary',
      aguardando_evidencia: 'secondary',
      aguardando_problema: 'secondary',
      aguardando_implantacao: 'secondary',
      resolvido: 'success',
      cancelado: 'danger',
      fechado: 'dark',
    };
    return colors[status] || 'secondary';
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      novo: 'Aberto',
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      aguardando_informacao_usuario: 'Aguardando Informação do Usuário',
      aguardando_terceiros: 'Aguardando Terceiros',
      aguardando_evidencia: 'Aguardando Evidência',
      aguardando_problema: 'Aguardando Problema',
      aguardando_implantacao: 'Aguardando Implantação',
      resolvido: 'Resolvido',
      cancelado: 'Cancelado',
      fechado: 'Fechado',
    };
    return labels[status] || status;
  };

  const columns: Column<IncidentListItem>[] = useMemo(() => {
    const isPurchaseSubtask = (item: IncidentListItem): item is PurchaseRequest =>
      (item as PurchaseRequest).item_source === 'purchase_subtask';

    const cols: Column<IncidentListItem>[] = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (ticket) =>
        isPurchaseSubtask(ticket) ? `#${getPurchaseDisplayId(ticket)}` : `#${ticket.id}`,
    },
    {
      key: 'type',
      label: 'Tipo',
      sortable: false,
      render: (ticket) => (
        <Badge
          bg={isPurchaseSubtask(ticket) || (ticket as Ticket).parent_ticket_id ? 'info' : 'secondary'}
          className="dashboard-badge"
        >
          {isPurchaseSubtask(ticket)
            ? 'Subtarefa de Compra'
            : (ticket as Ticket).parent_ticket_id
              ? 'Subtarefa'
              : 'Ticket'}
        </Badge>
      ),
    },
    {
      key: 'title',
      label: 'Título',
      sortable: true,
      render: (ticket) => (
        <div>
          <span>{ticket.title}</span>
          {(ticket as Ticket).parent_ticket_id && (
            <div className="mt-1">
              <small className="text-muted">
                Ticket pai:{' '}
                <Link to={`/tickets/${(ticket as Ticket).parent_ticket_id}`}>
                  #{(ticket as Ticket).parent_ticket_id}
                </Link>
              </small>
            </div>
          )}
          {isPurchaseSubtask(ticket) && ticket.parent_request_id && (
            <div className="mt-1">
              <small className="text-muted">
                Requisição pai:{' '}
                <Link to={`/purchases/${ticket.parent_request_id}`}>
                  #{ticket.parent_public_id || ticket.parent_request_id}
                </Link>
              </small>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (ticket) => (
        <Badge bg={getStatusColor(ticket.status)}>
          {getStatusLabel(ticket.status)}
        </Badge>
      ),
    },
    {
      key: 'urgency',
      label: 'Urgência',
      sortable: true,
      render: (ticket) => (
        <Badge bg={getUrgencyColor(ticket.urgency)}>
          {getUrgencyLabel(ticket.urgency)}
        </Badge>
      ),
    },
    ];
    if (canSeeTicketPriority) {
      cols.push({
        key: 'priority',
        label: 'Prioridade',
        sortable: true,
        render: (ticket) =>
          ticket.priority ? (
            <Badge bg={getPriorityColor(ticket.priority)} className="dashboard-badge">
              {getPriorityLabel(ticket.priority)}
            </Badge>
          ) : (
            <span className="text-muted small">—</span>
          ),
      });
    }
    cols.push(
    {
      key: 'creator',
      label: 'Criado por',
      sortable: false,
      render: (ticket) => ticket.creator?.name || 'N/A',
    },
    {
      key: 'created_at',
      label: 'Data',
      sortable: true,
      render: (ticket) => new Date(ticket.created_at).toLocaleDateString('pt-BR'),
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (ticket) => (
        <Button
          variant="outline-primary"
          size="sm"
          className="dashboard-filter-action-btn"
          onClick={() =>
            navigate(isPurchaseSubtask(ticket) ? `/purchases/${ticket.id}` : `/tickets/${ticket.id}`)
          }
        >
          Ver
        </Button>
      ),
    },
    );
    return cols;
  }, [canSeeTicketPriority, navigate]);
  // Padrões da tela: só viram chip quando o usuário se afasta deles.
  const thirdPartyDefault = user ? user.include_third_party_tickets !== false : true;
  const subtasksDefault = currentArea === 'tickets';

  const activeFilters: ActiveFilter[] = [];
  {
    const selectedWorkflow = workflows.find((w) => String(w.id) === filterWorkflow);
    const selectedGroup = groups.find((g) => String(g.id) === filterGroup);
    if (searchText.trim()) {
      activeFilters.push({
        id: 'search',
        label: `Texto: "${searchText.trim()}"`,
        onRemove: () => {
          setSearchText('');
          setSearchInputText('');
        },
      });
    }
    if (filterWorkflow) {
      activeFilters.push({
        id: 'workflow',
        label: `Fluxo: ${selectedWorkflow?.name || `#${filterWorkflow}`}`,
        onRemove: () => setFilterWorkflow(''),
      });
    }
    if (filterGroup) {
      activeFilters.push({
        id: 'group',
        label: `Grupo: ${selectedGroup?.name || `#${filterGroup}`}`,
        onRemove: () => setFilterGroup(''),
      });
    }
    if (filterAssignedToPeerId != null) {
      const peer = myGroupPeerOptions.find((p) => p.user_id === filterAssignedToPeerId);
      activeFilters.push({
        id: 'assigned-peer',
        label: `Atribuído a: ${peer?.user_name || `#${filterAssignedToPeerId}`}`,
        onRemove: () => clearAssignedPeer(),
      });
    }
    statusesForArea('tickets', filterStatuses).forEach((statusValue) => {
      activeFilters.push({
        id: `status-${statusValue}`,
        label: `Status: ${getStatusLabel(statusValue)}`,
        onRemove: () => setFilterStatuses((current) => current.filter((item) => item !== statusValue)),
      });
    });
    if (filterUrgency) {
      activeFilters.push({
        id: 'urgency',
        label: `Urgência: ${getUrgencyLabel(filterUrgency)}`,
        onRemove: () => setFilterUrgency(''),
      });
    }
    if (canSeeTicketPriority && filterPriority) {
      activeFilters.push({
        id: 'priority',
        label: `Prioridade: ${getPriorityLabel(filterPriority)}`,
        onRemove: () => setFilterPriority(''),
      });
    }
    if (filterCreatedFrom) {
      activeFilters.push({
        id: 'created-from',
        label: `Criado de: ${filterCreatedFrom}`,
        onRemove: () => setFilterCreatedFrom(''),
      });
    }
    if (filterCreatedTo) {
      activeFilters.push({
        id: 'created-to',
        label: `Criado até: ${filterCreatedTo}`,
        onRemove: () => setFilterCreatedTo(''),
      });
    }
    const closedVisibilityChip = closedVisibilityChipLabel(closedVisibility);
    if (closedVisibilityChip) {
      activeFilters.push({
        id: 'closed-visibility',
        label: closedVisibilityChip,
        onRemove: () => setClosedVisibility(DEFAULT_CLOSED_VISIBILITY),
      });
    }
    if (user && includeThirdPartyTickets !== thirdPartyDefault) {
      activeFilters.push({
        id: 'third-party',
        label: `Terceiros: ${includeThirdPartyTickets ? 'Incluídos' : 'Ocultos'}`,
        onRemove: () => handleThirdPartyFilterChange(thirdPartyDefault),
      });
    }
    if (showSubtasks !== subtasksDefault) {
      activeFilters.push({
        id: 'subtasks',
        label: `Subtarefas: ${showSubtasks ? 'Incluídas' : 'Ocultas'}`,
        onRemove: () => setShowSubtasks(subtasksDefault),
      });
    }
    if (filterCreatedByMe) {
      activeFilters.push({
        id: 'created-by-me',
        label: 'Apenas itens criados por mim',
        onRemove: () => setFilterCreatedByMe(false),
      });
    }
  }

  const closedVisibilityMessage = useMemo(
    () =>
      closedVisibilityNotice(closedVisibility, hiddenConcludedTotal, {
        singular: 'chamado',
        plural: 'chamados',
      }),
    [closedVisibility, hiddenConcludedTotal]
  );

  const ticketsPageStats = useMemo(() => {
    let open = 0;
    let inProgress = 0;
    let resolved = 0;

    for (const ticket of tickets) {
      if (ticket.status === 'novo' || ticket.status === 'aberto') open += 1;
      if (ticket.status === 'resolvido' || ticket.status === 'fechado') resolved += 1;
      if (ticket.status === 'em_andamento' || ticket.status.startsWith('aguardando_')) inProgress += 1;
    }

    return { open, inProgress, resolved };
  }, [tickets]);

  const handleThirdPartyFilterChange = async (checked: boolean) => {
    const previous = includeThirdPartyTickets;
    setIncludeThirdPartyTickets(checked);
    try {
      await axios.put(
        `${API_URL}/api/auth/me/include-third-party-tickets?include_third_party_tickets=${checked}`
      );
      await refreshUser();
    } catch (error) {
      setIncludeThirdPartyTickets(previous);
      console.error('Erro ao salvar preferência de terceiros:', error);
      showAlert({
        title: 'Aviso',
        message: 'Não foi possível salvar o padrão de visualização de chamados de terceiros.',
        variant: 'warning',
      });
    }
  };

  const applySearchFilter = () => {
    setSearchText(searchInputText.trim());
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterStatuses([]);
    setFilterUrgency('');
    setFilterPriority('');
    setFilterWorkflow('');
    setFilterGroup('');
    setShowSubtasks(currentArea === 'tickets');
    clearAssignedPeer();
    setFilterCreatedFrom('');
    setFilterCreatedTo('');
    setClosedVisibility(DEFAULT_CLOSED_VISIBILITY);
    setFilterCreatedByMe(false);
    setSearchText('');
    setSearchInputText('');
    setCurrentPage(1);
    if (user) {
      setIncludeThirdPartyTickets(user.include_third_party_tickets !== false);
    }
    sessionStorage.removeItem(ticketsFiltersSessionKey(currentArea));
    navigate('/tickets', { replace: true });
  };

  return (
    <>
      <AlertComponent />
      <div className="dashboard-page">
        <div className="dashboard-hero mb-3">
          <div className="d-flex justify-content-between align-items-center dashboard-header">
            <div>
              <h2 className="dashboard-title mb-1">Tickets</h2>
              <small className="text-muted">
                Lista completa de chamados com busca, filtros e ordenação.
              </small>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <Button size="sm" variant="success" onClick={() => setShowModal(true)}>
                + Novo Ticket
              </Button>
            </div>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col sm={6} xl={3}>
            <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
              <Card.Body>
                <p className="dashboard-stat-title mb-1">Total filtrado</p>
                <h4 className="dashboard-stat-value text-primary">{totalTickets}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
              <Card.Body>
                <p className="dashboard-stat-title mb-1">Abertos</p>
                <h4 className="dashboard-stat-value text-warning">{ticketsPageStats.open}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
              <Card.Body>
                <p className="dashboard-stat-title mb-1">Em andamento</p>
                <h4 className="dashboard-stat-value text-info">{ticketsPageStats.inProgress}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col sm={6} xl={3}>
            <Card className="dashboard-stat-card dashboard-section-card h-100 mb-0">
              <Card.Body>
                <p className="dashboard-stat-title mb-1">Resolvidos</p>
                <h4 className="dashboard-stat-value text-success">{ticketsPageStats.resolved}</h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <FilterPanel
          panelId="tickets-filters-panel"
          activeFilters={activeFilters}
          expanded={showFilters}
          onToggleExpanded={() => setShowFilters((prev) => !prev)}
          onClearAll={handleClearFilters}
          notice={closedVisibilityMessage}
        >
          <FilterSection title="Busca e escopo">
            <FilterField label="Texto" controlId="tickets-filter-search" wide>
              <InputGroup size="sm">
                <Form.Control
                  id="tickets-filter-search"
                  type="text"
                  placeholder="Título ou descrição; para ID use #número"
                  value={searchInputText}
                  onChange={(e) => setSearchInputText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applySearchFilter();
                    }
                  }}
                />
                <Button variant="outline-secondary" onClick={applySearchFilter} title="Aplicar busca">
                  Buscar
                </Button>
              </InputGroup>
            </FilterField>
            <FilterField label="Fluxo" controlId="tickets-filter-workflow">
              <FilterDropdownSelect
                id="tickets-filter-workflow"
                value={filterWorkflow}
                onChange={setFilterWorkflow}
                options={workflowFilterOptions}
                placeholderLabel="Todos"
              />
            </FilterField>
            <FilterField label="Grupo" controlId="tickets-filter-group">
              <FilterDropdownSelect
                id="tickets-filter-group"
                value={filterGroup}
                onChange={setFilterGroup}
                options={groupFilterOptions}
                placeholderLabel="Todos"
              />
            </FilterField>
            <FilterField label="Membro do grupo" controlId="tickets-filter-peer">
              <FilterAssigneeCombobox
                id="tickets-filter-peer"
                peerOptions={myGroupPeerOptions}
                filteredPeers={filteredPeerSuggestions}
                searchText={assignedPeerSearch}
                onSearchTextChange={(value) => {
                  setAssignedPeerSearch(value);
                  setFilterAssignedToPeerId(null);
                }}
                selectedPeerId={filterAssignedToPeerId}
                onSelectPeer={(peer: AssigneePeerOption) => selectAssignedPeer(peer)}
                onClearPeer={clearAssignedPeer}
              />
            </FilterField>
          </FilterSection>

          <FilterSection title="Refinar resultados">
            <FilterField
              label="Status"
              controlId="tickets-filter-status"
              hint="Segure Ctrl (Cmd no Mac) e clique para combinar status. Sem seleção: todos."
            >
              <StatusMultiFilter
                idPrefix="tickets-filter-status"
                area="tickets"
                selected={statusesForArea('tickets', filterStatuses)}
                onChange={setFilterStatuses}
              />
            </FilterField>
            <FilterField label="Urgência" controlId="tickets-filter-urgency">
              <FilterDropdownSelect
                id="tickets-filter-urgency"
                value={filterUrgency}
                onChange={setFilterUrgency}
                options={URGENCY_OPTIONS}
                placeholderLabel="Todas"
              />
            </FilterField>
            {canSeeTicketPriority ? (
              <FilterField label="Prioridade" controlId="tickets-filter-priority">
                <FilterDropdownSelect
                  id="tickets-filter-priority"
                  value={filterPriority}
                  onChange={setFilterPriority}
                  options={priorityFilterOptions}
                  placeholderLabel="Todas"
                />
              </FilterField>
            ) : null}
            <FilterField label="Criado de" controlId="tickets-filter-created-from">
              <FilterInputGroup>
                <FilterControl
                  id="tickets-filter-created-from"
                  type="date"
                  value={filterCreatedFrom}
                  onChange={(e) => setFilterCreatedFrom(e.target.value)}
                />
                {filterCreatedFrom ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="px-2"
                    title="Limpar data inicial"
                    aria-label="Limpar Criado de"
                    onClick={() => setFilterCreatedFrom('')}
                  >
                    ×
                  </Button>
                ) : null}
              </FilterInputGroup>
            </FilterField>
            <FilterField label="Criado até" controlId="tickets-filter-created-to">
              <FilterInputGroup>
                <FilterControl
                  id="tickets-filter-created-to"
                  type="date"
                  value={filterCreatedTo}
                  onChange={(e) => setFilterCreatedTo(e.target.value)}
                />
                {filterCreatedTo ? (
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="px-2"
                    title="Limpar data final"
                    aria-label="Limpar Criado até"
                    onClick={() => setFilterCreatedTo('')}
                  >
                    ×
                  </Button>
                ) : null}
              </FilterInputGroup>
            </FilterField>
            <FilterField
              label="Conclusão"
              controlId="tickets-filter-closed-visibility"
              hint="Vale para chamados fechados e cancelados, pela data de conclusão."
            >
              <FilterDropdownSelect
                id="tickets-filter-closed-visibility"
                value={closedVisibility}
                onChange={(value) => setClosedVisibility(normalizeClosedVisibility(value))}
                options={closedVisibilityFilterOptions}
                showEmptyOption={false}
              />
            </FilterField>
          </FilterSection>

          <FilterSection title="Exibição" layout="inline">
            {user ? (
              <FilterToggle
                id="tickets-filter-third-party"
                label={includeThirdPartyTickets ? 'Incluir chamados de terceiros' : 'Ocultar chamados de terceiros'}
                checked={includeThirdPartyTickets}
                onChange={handleThirdPartyFilterChange}
              />
            ) : null}
            <FilterToggle
              id="tickets-filter-subtasks"
              label={showSubtasks ? 'Incluir subtarefas' : 'Ocultar subtarefas'}
              checked={showSubtasks}
              onChange={setShowSubtasks}
            />
            <FilterToggle
              id="tickets-filter-created-by-me"
              label={filterCreatedByMe ? 'Apenas itens criados por mim' : 'Mostrar itens de todos os autores'}
              checked={filterCreatedByMe}
              onChange={setFilterCreatedByMe}
            />
          </FilterSection>
        </FilterPanel>

        <Card className="dashboard-section-card">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex flex-column">
              <span>Todos os Tickets ({totalTickets})</span>
              <small className="text-muted">Ordenação e paginação abaixo da tabela.</small>
            </div>
          </Card.Header>
          <Card.Body>
            <DataTable
              className="tickets-datatable"
              data={tickets}
              columns={columns}
              loading={loading}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalTickets}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              emptyMessage="Nenhum ticket encontrado"
              tableClassName="dashboard-table table-sm align-middle mb-0"
            />
          </Card.Body>
        </Card>
      </div>

      <TicketModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSuccess={fetchTickets}
        workflows={workflows}
        groups={groups}
        showAlert={showAlert}
      />
    </>
  );
};

interface TicketModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  workflows: Workflow[];
  groups: Group[];
  showAlert: (options: { title: string; message: string; variant?: 'success' | 'danger' | 'warning' | 'info'; details?: string }) => void;
}

const TicketModal: React.FC<TicketModalProps> = ({
  show,
  onHide,
  onSuccess,
  workflows,
  groups,
  showAlert,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urgency, setUrgency] = useState<TicketUrgency | ''>('');
  const [workflowId, setWorkflowId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<{ [fieldId: number]: any }>({});
  const [customFieldFiles, setCustomFieldFiles] = useState<{ [fieldId: number]: File | File[] | null }>({});
  const [users, setUsers] = useState<User[]>([]);
  const [analystUsers, setAnalystUsers] = useState<User[]>([]);
  const [searchUsers, setSearchUsers] = useState<{ [fieldId: number]: string }>({});
  const [filteredUsers, setFilteredUsers] = useState<{ [fieldId: number]: User[] }>({});
  const [requesterId, setRequesterId] = useState<number | null>(null);
  const [requesterSearchTerm, setRequesterSearchTerm] = useState<string>('');
  const [debouncedRequesterQuery, setDebouncedRequesterQuery] = useState<string>('');
  const [filteredRequesterUsers, setFilteredRequesterUsers] = useState<User[]>([]);
  const [requesterSearchLoading, setRequesterSearchLoading] = useState(false);
  const [selectedRequester, setSelectedRequester] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('informacoes');
  const [templateOptions, setTemplateOptions] = useState<TicketTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateCategories, setTemplateCategories] = useState<string[]>([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateFilterCategory, setTemplateFilterCategory] = useState('');
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);
  const { user: currentUser } = useAuth();

  const initializeCustomFieldValues = useCallback(
    (fields: WorkflowCustomField[], preserveExistingValues: boolean) => {
      setCustomFieldValues((prevValues) => {
        const nextValues: { [fieldId: number]: any } = preserveExistingValues ? { ...prevValues } : {};

        fields.forEach((field) => {
          if (preserveExistingValues && field.id in nextValues) {
            return;
          }

          if (field.field_type === 'list' || field.field_type === 'combo') {
            nextValues[field.id] = field.allow_multiple ? [] : '';
          } else if (field.field_type === 'table') {
            nextValues[field.id] = createDefaultTableValue();
          } else if (field.field_type === 'currency') {
            nextValues[field.id] = JSON.stringify({ currency: 'BRL', value: '' });
          } else if (field.field_type === 'period') {
            nextValues[field.id] = JSON.stringify({ start: '', end: '' });
          } else {
            nextValues[field.id] = '';
          }
        });

        return nextValues;
      });
    },
    []
  );

  const loadCustomFieldsForWorkflow = useCallback(
    async (selectedWorkflowId: string, selectedGroupId?: string, preserveExistingValues = false) => {
      if (!selectedWorkflowId) {
        setCustomFields([]);
        setCustomFieldValues({});
        return;
      }

      try {
        const params = selectedGroupId ? { group_id: selectedGroupId } : undefined;
        const fieldsResponse = await axios.get(`${API_URL}/api/workflows/${selectedWorkflowId}/custom-fields/first-step`, {
          params
        });
        const fields = fieldsResponse.data || [];
        setCustomFields(fields);
        initializeCustomFieldValues(fields, preserveExistingValues);
      } catch (error: any) {
        console.error('Erro ao carregar campos customizados:', error);
        console.error('Detalhes do erro:', error.response?.data);
        setCustomFields([]);
        setCustomFieldValues({});
      }
    },
    [initializeCustomFieldValues]
  );

  // Carregar grupos do Primeiro Contato baseado no workflow selecionado
  React.useEffect(() => {
    const loadGroupsForWorkflow = async () => {
      if (workflowId) {
        try {
          // Buscar grupos do Primeiro Contato do workflow
          const response = await axios.get(`${API_URL}/api/workflows/${workflowId}/first-contact-groups`);
          // Filtrar grupos ativos e garantir que tenham nome válido
          const validGroups = (response.data || []).filter((g: Group) => {
            const isActive = g.is_active !== false; // Considerar ativo se não for explicitamente false
            const hasName = g.name && g.name.trim() !== '';
            return isActive && hasName;
          });
          const preservedGroupId = validGroups.some((g: Group) => String(g.id) === groupId) ? groupId : '';
          setAvailableGroups(validGroups);
          setGroupId(preservedGroupId);
          setCustomFieldFiles({});
          setActiveTab('informacoes');
          await loadCustomFieldsForWorkflow(workflowId, preservedGroupId || undefined, !!preservedGroupId);
          
          if (validGroups.length === 0 && (response.data || []).length > 0) {
            console.warn('Nenhum grupo válido encontrado para o workflow. Grupos recebidos:', response.data);
          }
        } catch (error: any) {
          console.error('Erro ao carregar grupos do Primeiro Contato:', error);
          console.error('Detalhes do erro:', error.response?.data);
          setAvailableGroups([]);
          setCustomFields([]);
          setCustomFieldValues({});
        }
      } else {
        setAvailableGroups([]);
        setGroupId('');
        setCustomFields([]);
        setCustomFieldValues({});
        setCustomFieldFiles({});
        setActiveTab('informacoes');
      }
    };

    loadGroupsForWorkflow();
  }, [loadCustomFieldsForWorkflow, workflowId]);

  React.useEffect(() => {
    if (!workflowId || !groupId) return;
    void loadCustomFieldsForWorkflow(workflowId, groupId, false);
  }, [groupId, loadCustomFieldsForWorkflow, workflowId]);

  // Debounce do texto do solicitante para consultar /api/users/search
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedRequesterQuery(requesterSearchTerm);
    }, 350);
    return () => window.clearTimeout(t);
  }, [requesterSearchTerm]);

  // Busca servidor: solicitante (ao menos 1 caractere após trim)
  React.useEffect(() => {
    const q = debouncedRequesterQuery.trim();
    if (q.length < 1) {
      setFilteredRequesterUsers([]);
      setRequesterSearchLoading(false);
      return;
    }
    const ac = new AbortController();
    setRequesterSearchLoading(true);
    (async () => {
      try {
        const items = await searchActiveUsers(q, getAuthToken(), {
          limit: 25,
          signal: ac.signal,
        });
        setFilteredRequesterUsers(items);
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setFilteredRequesterUsers([]);
      } finally {
        setRequesterSearchLoading(false);
      }
    })();
    return () => ac.abort();
  }, [debouncedRequesterQuery]);

  // Carregar usuários e analistas quando necessário
  React.useEffect(() => {
    const hasUserField = customFields.some(f => f.field_type === 'user');
    const hasAnalystField = customFields.some(f => f.field_type === 'analyst');

    const fetchData = async () => {
      if (hasUserField) {
        try {
          const response = await axios.get(`${API_URL}/api/users`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          });
          setUsers(response.data.filter((u: User) => u.is_active));
        } catch (error) {
          console.error('Erro ao buscar usuários:', error);
        }
      }

      if (hasAnalystField && groupId) {
        try {
          const response = await axios.get(`${API_URL}/api/groups/${groupId}`);
          const groupMembers = response.data.members || [];
          setAnalystUsers(groupMembers.map((m: any) => ({
            id: m.user_id,
            name: m.user_name,
            email: m.user_email,
            is_active: true
          })));
        } catch (error) {
          console.error('Erro ao buscar analistas do grupo:', error);
        }
      }
    };

    if (customFields.length > 0) {
      fetchData();
    }
  }, [customFields, groupId]);

  const refreshTemplateData = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const [categories, templates] = await Promise.all([
        listTicketTemplateCategories(),
        listTicketTemplates(),
      ]);
      setTemplateCategories(categories);
      setTemplateOptions(templates);
    } catch (error) {
      console.error('Erro ao carregar dados de template:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  React.useEffect(() => {
    if (!show) return;
    refreshTemplateData();
  }, [show, refreshTemplateData]);

  const handleCustomFieldChange = (fieldId: number, value: any) => {
    setCustomFieldValues({ ...customFieldValues, [fieldId]: value });
  };

  const applyTemplate = (template: TicketTemplate) => {
    setTitle(template.title || '');
    setDescription(template.description || '');
    setTemplateCategory(template.category || '');
    if (template.urgency) {
      setUrgency(template.urgency);
    }
    if (template.workflow_id) {
      setWorkflowId(String(template.workflow_id));
    }
    if (template.assigned_group_id) {
      setGroupId(String(template.assigned_group_id));
    }
    if (template.requester_id) {
      setRequesterId(template.requester_id);
    } else {
      setRequesterId(null);
      setSelectedRequester(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setFilteredRequesterUsers([]);
    }
    showAlert({
      title: 'Template aplicado',
      message: 'Campos do template aplicados com sucesso.',
      variant: 'success',
    });
  };

  const handleSaveTemplate = async () => {
    if (savingTemplate) return;

    const normalizedTitle = title.trim();
    const normalizedDescription = description.trim();
    const normalizedCategory = templateCategory.trim();

    if (!normalizedTitle || !normalizedDescription) {
      showAlert({
        title: 'Dados insuficientes',
        message: 'Preencha título e descrição antes de salvar como template.',
        variant: 'warning',
      });
      return;
    }

    setSavingTemplate(true);
    try {
      await createTicketTemplate({
        title: normalizedTitle,
        description: normalizedDescription,
        category: normalizedCategory || null,
        urgency: urgency || null,
        priority: null,
        requester_id: requesterId || null,
        assigned_group_id: groupId ? parseInt(groupId) : null,
        workflow_id: workflowId ? parseInt(workflowId) : null,
      });
      await refreshTemplateData();
      if (normalizedCategory && !templateCategories.includes(normalizedCategory)) {
        setTemplateCategories((prev) => [...prev, normalizedCategory].sort((a, b) => a.localeCompare(b, 'pt-BR')));
      }
      showAlert({
        title: 'Template salvo',
        message: 'Template de chamado salvo com sucesso.',
        variant: 'success',
      });
      setShowSaveTemplateModal(false);
    } catch (error: any) {
      showAlert({
        title: 'Erro ao salvar template',
        message: 'Não foi possível salvar o template.',
        variant: 'danger',
        details: error?.response?.data?.detail || 'Erro desconhecido',
      });
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    setDeletingTemplateId(templateId);
    try {
      await deleteTicketTemplate(templateId);
      await refreshTemplateData();
      showAlert({
        title: 'Template removido',
        message: 'Template excluído com sucesso.',
        variant: 'success',
      });
    } catch (error: any) {
      showAlert({
        title: 'Erro ao excluir template',
        message: 'Não foi possível excluir o template selecionado.',
        variant: 'danger',
        details: error?.response?.data?.detail || 'Erro desconhecido',
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const filteredTemplateOptions = React.useMemo(() => {
    const search = templateSearchTerm.trim().toLowerCase();
    return templateOptions.filter((item) => {
      const byCategory = templateFilterCategory ? item.category === templateFilterCategory : true;
      if (!byCategory) return false;
      if (!search) return true;
      return (
        item.title.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search) ||
        (item.category || '').toLowerCase().includes(search)
      );
    });
  }, [templateOptions, templateSearchTerm, templateFilterCategory]);

  // Função para formatar valor monetário brasileiro
  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      'BRL': 'R$',
      'USD': '$',
      'EUR': '€'
    };
    return symbols[currency] || currency;
  };

  const formatCurrency = (value: string | number, currency: string = 'BRL'): string => {
    if (!value) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
    return `${getCurrencySymbol(currency)} ${formatted}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Proteção contra duplo clique - retornar se já estiver processando
    if (loading) {
      return;
    }
    
    // Validação de campos obrigatórios
    type ValidationError = {
      message: string;
      tabKey: string;
    };
    const errors: ValidationError[] = [];

    const hasAnyCustomGroup = customFields.some((field) => {
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      return isVisible && !!field.group_name?.trim();
    });

    const getCustomFieldTabName = (field: WorkflowCustomField): string => {
      const groupName = field.group_name?.trim();
      if (groupName) return groupName;
      return hasAnyCustomGroup ? 'Geral' : 'Campos Customizados';
    };

    const getRequiredFieldMessage = (fieldName: string, tabName?: string): string =>
      tabName
        ? `Preencha o campo "${fieldName}" na aba "${tabName}"`
        : `Preencha o campo "${fieldName}"`;

    customFields.forEach((field) => {
      if (field.field_type !== 'user') return;

      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      if (isBlocked || !isVisible) return;

      const userSearchTerm = (searchUsers[field.id] || '').trim();
      const selectedUserId = Number(customFieldValues[field.id]);
      const hasSelectedUser = users.some((u) => u.id === selectedUserId);

      if ((userSearchTerm && !hasSelectedUser) || (!userSearchTerm && customFieldValues[field.id] && !hasSelectedUser)) {
        const tabName = getCustomFieldTabName(field);
        errors.push({
          message: `Selecione um usuário cadastrado para o campo "${field.label?.trim() || field.name?.trim() || `ID ${field.id}`}" na aba "${tabName}"`,
          tabKey: field.group_name || 'geral',
        });
      }
    });
    
    if (!workflowId) {
      errors.push({
        message: getRequiredFieldMessage('Fluxo de Trabalho', 'Informações'),
        tabKey: 'informacoes',
      });
    }
    
    if (!title || title.trim() === '') {
      errors.push({
        message: getRequiredFieldMessage('Título', 'Informações'),
        tabKey: 'informacoes',
      });
    }
    
    if (!description || description.trim() === '') {
      errors.push({
        message: getRequiredFieldMessage('Descrição', 'Informações'),
        tabKey: 'informacoes',
      });
    }

    if (!urgency) {
      errors.push({
        message: getRequiredFieldMessage('Urgência', 'Informações'),
        tabKey: 'informacoes',
      });
    }
    
    if (!groupId) {
      errors.push({
        message: getRequiredFieldMessage('Grupo para Encaminhar', 'Informações'),
        tabKey: 'informacoes',
      });
    }
    
    // Validação de campos customizados obrigatórios
    customFields.forEach((field) => {
      const isRequired = (field.step_is_required !== undefined && field.step_is_required !== null) 
        ? !!field.step_is_required
        : !!field.is_required;
      const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
      const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
      
      // Só validar campos visíveis, não bloqueados e obrigatórios
      if (isRequired && !isBlocked && isVisible) {
        // Item 38: obrigatoriedade só vale se a condição de exibição for atendida
        if (!shouldShowConditionalField(field, customFields, customFieldValues)) return;
        if (field.field_type === 'file' || field.field_type === 'files') {
          const fieldFile = customFieldFiles[field.id];
          if (!fieldFile) {
            const tabName = getCustomFieldTabName(field);
            errors.push({
              message: getRequiredFieldMessage(field.label?.trim() || field.name?.trim() || `ID ${field.id}`, tabName),
              tabKey: field.group_name || 'geral',
            });
          }
        } else {
          const fieldValue = customFieldValues[field.id];
          const isEmpty = fieldValue === undefined || 
                          fieldValue === null || 
                          fieldValue === '' ||
                          (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                          (field.field_type === 'table' && !hasTableContent(fieldValue));
          
          if (isEmpty) {
            const tabName = getCustomFieldTabName(field);
            errors.push({
              message: getRequiredFieldMessage(field.label?.trim() || field.name?.trim() || `ID ${field.id}`, tabName),
              tabKey: field.group_name || 'geral',
            });
          }
        }
      }
    });
    
    // Se houver erros, exibir mensagem
    if (errors.length > 0) {
      const tabToSelect = errors[0].tabKey;
      const detailsText = errors.map(error => `• ${error.message}`).join('\n');
      setActiveTab(tabToSelect);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          showAlert({
            title: 'Campos Obrigatórios Não Preenchidos',
            message: 'Revise os campos pendentes abaixo:',
            variant: 'warning',
            details: detailsText
          });
        });
      });
      return;
    }
    
    setLoading(true);

    try {
      // Criar o ticket
      const ticketData: any = {
        title,
        description,
        urgency,
        workflow_id: parseInt(workflowId),
        assigned_group_id: parseInt(groupId),
      };

      // Adicionar requester_id se um solicitante foi selecionado
      if (requesterId) {
        ticketData.requester_id = requesterId;
      }
      
      const ticketResponse = await axios.post(`${API_URL}/api/tickets/`, ticketData);
      
      const ticketId = ticketResponse.data.id;
      
      // Falhas de campo não impedem a criação do ticket, mas precisam ser reportadas:
      // salvar em silêncio faria o usuário acreditar que o dado foi persistido.
      const persistenceErrors: string[] = [];
      const describeFieldError = (field: WorkflowCustomField, error: unknown): string => {
        const label = field.label?.trim() || field.name?.trim() || `ID ${field.id}`;
        const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
          ?.detail;
        return `${label}: ${typeof detail === 'string' ? detail : 'não foi possível salvar'}`;
      };

      // Salvar valores dos campos customizados em paralelo (muito mais rápido)
      const customFieldPromises = customFields
        .filter(field => {
          const fieldValue = customFieldValues[field.id];
          return fieldValue !== undefined && fieldValue !== '' && fieldValue !== null && 
                 !(Array.isArray(fieldValue) && fieldValue.length === 0) &&
                 !(field.field_type === 'table' && !hasTableContent(fieldValue)) &&
                 field.field_type !== 'file' && field.field_type !== 'files'; // Arquivos são tratados separadamente
        })
        .map(field => {
          const fieldValue = customFieldValues[field.id];
          const valueData: any = {
            ticket_id: ticketId,
            custom_field_id: field.id,
          };
          
          // Tratar diferentes tipos de campos
          if (field.field_type === 'table') {
            valueData.text_value = serializeTableValue(fieldValue);
          } else if (field.field_type === 'currency' || field.field_type === 'period') {
            // Campos que são salvos como JSON
            valueData.text_value = typeof fieldValue === 'string' ? fieldValue : JSON.stringify(fieldValue);
          } else if (field.field_type === 'list' || field.field_type === 'combo') {
            // Campos de lista/combo
            valueData.text_value = field.allow_multiple && Array.isArray(fieldValue)
              ? JSON.stringify(fieldValue)
              : String(fieldValue);
          } else {
            // Campos de texto, número, data, link, user, analyst, etc.
            valueData.text_value = String(fieldValue);
          }
          
          return upsertCustomFieldValue(API_URL, 'tickets', ticketId, valueData).catch((error: unknown) => {
              console.error(`Erro ao salvar campo customizado ${field.id}:`, error);
              // Não bloquear a criação do ticket se houver erro ao salvar campos customizados
              persistenceErrors.push(describeFieldError(field, error));
              return null;
            });
        });
      
      // Aguardar todos os campos serem salvos em paralelo
      await Promise.all(customFieldPromises);

      // Fazer upload de arquivos dos campos customizados
      const fileUploadPromises = customFields
        .filter(field => (field.field_type === 'file' || field.field_type === 'files') && customFieldFiles[field.id])
        .map(async (field) => {
          const fieldFile = customFieldFiles[field.id];
          if (!fieldFile) return;

          try {
            if (field.field_type === 'file') {
              const formData = new FormData();
              formData.append('file', fieldFile as File);

              await axios.post(
                `${API_URL}/api/tickets/${ticketId}/custom-fields/${field.id}/upload`,
                formData,
                {
                  headers: { 'Content-Type': 'multipart/form-data' },
                }
              );
            } else if (field.field_type === 'files') {
              const formData = new FormData();
              if (Array.isArray(fieldFile)) {
                fieldFile.forEach((f) => formData.append('files', f));
              } else {
                formData.append('files', fieldFile as File);
              }

              await axios.post(
                `${API_URL}/api/tickets/${ticketId}/custom-fields/${field.id}/upload-multiple`,
                formData,
                {
                  headers: { 'Content-Type': 'multipart/form-data' },
                }
              );
            }
          } catch (error) {
            console.error(`Erro ao fazer upload do arquivo para campo ${field.id}:`, error);
            persistenceErrors.push(describeFieldError(field, error));
          }
        });
      
      await Promise.all(fileUploadPromises);
      
      // Limpar campos primeiro
      setTitle('');
      setDescription('');
      setUrgency('');
      setWorkflowId('');
      setGroupId('');
      setRequesterId(null);
      setRequesterSearchTerm('');
      setDebouncedRequesterQuery('');
      setSelectedRequester(null);
      setFilteredRequesterUsers([]);
      setCustomFields([]);
      setCustomFieldValues({});
      setCustomFieldFiles({});
      setTemplateCategory('');
      setActiveTab('informacoes');
      
      // Atualizar lista de tickets
      onSuccess();
      
      // Mostrar confirmação primeiro
      if (persistenceErrors.length > 0) {
        showAlert({
          title: 'Ticket criado com pendências',
          message: `O ticket foi criado, mas ${persistenceErrors.length} campo(s) não puderam ser salvos. Reabra o ticket e preencha novamente.`,
          variant: 'warning',
          details: persistenceErrors.join('\n')
        });
      } else {
        showAlert({
          title: 'Sucesso',
          message: 'Ticket criado com sucesso!',
          variant: 'success'
        });
      }
      
      // Fechar modal após um delay para garantir que o alerta seja exibido
      setTimeout(() => {
        onHide();
      }, 300);
    } catch (error: any) {
      console.error('Erro ao criar ticket:', error);
      showAlert({
        title: 'Erro ao Criar Ticket',
        message: 'Não foi possível criar o ticket.',
        variant: 'danger',
        details: error.response?.data?.detail || 'Erro desconhecido'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Novo Ticket</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Tabs
            activeKey={activeTab}
            onSelect={(tabKey) => setActiveTab(tabKey || 'informacoes')}
            className="mb-3"
          >
            <Tab eventKey="informacoes" title="Informações">
              <div className="pt-3">
                <Form.Group className="mb-3">
                  <Form.Label>Fluxo de Trabalho <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    value={workflowId} 
                    onChange={(e) => setWorkflowId(e.target.value)}
                    required
                    autoFocus
                  >
                    <option value="">Selecione um fluxo</option>
                    {workflows
                      .filter((w: Workflow) => w.is_active)
                      .sort((a: Workflow, b: Workflow) => a.name.localeCompare(b.name, 'pt-BR'))
                      .map((workflow: Workflow) => (
                        <option key={workflow.id} value={String(workflow.id)}>
                          {workflow.name}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Todo ticket deve estar associado a um fluxo de trabalho.
                  </Form.Text>
                  {workflowId && (() => {
                    const selectedWorkflow = workflows.find((w: Workflow) => String(w.id) === workflowId);
                    return selectedWorkflow ? (
                      <div className="mt-2 p-3 workflow-info-box rounded">
                        <strong className="workflow-info-title">Fluxo Selecionado:</strong> {selectedWorkflow.name}
                        {selectedWorkflow.description && (
                          <div className="mt-2">
                            <small className="workflow-info-description">{selectedWorkflow.description}</small>
                          </div>
                        )}
                      </div>
                    ) : null;
                  })()}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Título <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    disabled={!workflowId}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    disabled={!workflowId}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Urgência <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value as TicketUrgency)}
                    required
                    disabled={!workflowId}
                    aria-label="Selecione a urgência do chamado"
                  >
                    <option value="" disabled>Selecione a urgência</option>
                    {URGENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Informe o nível de urgência do seu chamado.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Solicitante</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      placeholder={currentUser ? `Padrão: ${currentUser.name} — digite para buscar outro` : 'Digite nome ou e-mail para buscar...'}
                      value={
                        requesterId && selectedRequester
                          ? `${selectedRequester.name} (${selectedRequester.email})`
                          : requesterSearchTerm
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setRequesterSearchTerm(v);
                        setRequesterId(null);
                        setSelectedRequester(null);
                        if (!v.trim()) {
                          setFilteredRequesterUsers([]);
                        }
                      }}
                      disabled={!workflowId}
                    />
                    {requesterSearchTerm.trim() && !requesterId && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        {(requesterSearchLoading ||
                          requesterSearchTerm.trim() !== debouncedRequesterQuery.trim()) && (
                          <div className="px-3 py-2 text-muted small">Buscando...</div>
                        )}
                        {!requesterSearchLoading &&
                          requesterSearchTerm.trim() === debouncedRequesterQuery.trim() &&
                          debouncedRequesterQuery.trim().length >= 1 &&
                          filteredRequesterUsers.length === 0 && (
                            <div className="px-3 py-2 text-muted small">Nenhum usuário encontrado</div>
                          )}
                        {!requesterSearchLoading &&
                          requesterSearchTerm.trim() === debouncedRequesterQuery.trim() &&
                          filteredRequesterUsers.map((user) => (
                            <div
                              key={user.id}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                              }}
                              onClick={() => {
                                setRequesterId(user.id);
                                setSelectedRequester(user);
                                setRequesterSearchTerm('');
                                setDebouncedRequesterQuery('');
                                setFilteredRequesterUsers([]);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              {user.name} ({user.email})
                            </div>
                          ))}
                      </div>
                    )}
                    {requesterId && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setRequesterId(null);
                          setSelectedRequester(null);
                          setRequesterSearchTerm('');
                          setDebouncedRequesterQuery('');
                          setFilteredRequesterUsers([]);
                        }}
                        style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)', padding: '0 5px' }}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Selecione o usuário que está solicitando o ticket. Se não selecionar, você será o solicitante.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label>Grupo para Encaminhar <span className="text-danger">*</span></Form.Label>
                  <Form.Select 
                    value={groupId} 
                    onChange={(e) => setGroupId(e.target.value)}
                    required={!(!workflowId || availableGroups.length === 0)}
                    disabled={!workflowId || availableGroups.length === 0}
                  >
                    <option value="">
                      {!workflowId 
                        ? 'Selecione um fluxo primeiro' 
                        : availableGroups.length === 0 
                          ? 'Nenhum grupo disponível para este fluxo'
                          : 'Selecione um grupo'}
                    </option>
                    {availableGroups.map((group: Group) => (
                      <option key={group.id} value={String(group.id)}>
                        {group.name || `Grupo #${group.id}`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {workflowId 
                      ? 'Selecione um grupo que atende ao Primeiro Contato deste fluxo de trabalho.'
                      : 'Todo ticket deve ser encaminhado para um grupo associado ao fluxo.'}
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>

            {customFields.length > 0 ? (() => {
            const shouldShowField = (field: WorkflowCustomField): boolean =>
              shouldShowConditionalField(field, customFields, customFieldValues);

            // Agrupar campos por group_name
            const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
            const ungroupedFields: WorkflowCustomField[] = [];

            customFields.forEach(field => {
              // Respeitar apenas visibilidade da etapa para montar estrutura de abas.
              // Campos condicionais são filtrados no conteúdo, não na existência da aba.
              const isVisible = field.step_is_visible !== undefined ? field.step_is_visible : true;
              if (!isVisible) return;

              if (field.group_name) {
                const groupName = field.group_name;
                if (!groupedFields[groupName]) {
                  groupedFields[groupName] = [];
                }
                groupedFields[groupName].push(field);
              } else {
                ungroupedFields.push(field);
              }
            });

            const hasGroups = Object.keys(groupedFields).length > 0;
            const hasUngrouped = ungroupedFields.length > 0;
            const totalGroups = Object.keys(groupedFields).length + (hasUngrouped ? 1 : 0);
            
            console.log('Agrupamento de campos:', {
              groupedFields: Object.keys(groupedFields),
              ungroupedCount: ungroupedFields.length,
              hasGroups,
              hasUngrouped,
              totalGroups,
              shouldShowTabs: hasGroups && (Object.keys(groupedFields).length > 1 || hasUngrouped)
            });

            // Função para renderizar um campo
            const renderField = (field: WorkflowCustomField) => {
              // Usar step_is_required se disponível e não for null, senão usar is_required
              const isRequired = (field.step_is_required !== undefined && field.step_is_required !== null) 
                ? !!field.step_is_required
                : !!field.is_required;
              // Determinar se o campo está bloqueado: usar step_is_blocked se disponível
              const isBlocked = field.step_is_blocked !== undefined ? field.step_is_blocked : false;
              const fieldValue = customFieldValues[field.id];
              const userSearchTerm = searchUsers[field.id] || '';
              
              return (
                <Form.Group key={field.id} className="mb-3">
                  <Form.Label>
                    {field.label}
                    {isRequired && <span className="text-danger ms-1">*</span>}
                  </Form.Label>
                  {field.description && (
                    <Form.Text className="text-muted d-block mb-2" style={{ whiteSpace: 'pre-line' }}>
                      {field.description}
                    </Form.Text>
                  )}
                  {field.field_type === 'text_short' ? (
                    <Form.Control
                      type="text"
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                    />
                  ) : field.field_type === 'text_long' ? (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                    />
                  ) : field.field_type === 'list' || field.field_type === 'combo' ? (
                    (() => {
                      const options: string[] = field.options 
                        ? field.options.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0)
                        : [];
                      if (field.allow_multiple) {
                        const selectedValues: string[] = Array.isArray(fieldValue) 
                          ? (fieldValue as string[])
                          : [];
                        return (
                          <div>
                            {options.map((option: string, idx: number) => {
                              const isChecked = selectedValues.includes(option);
                              return (
                                <Form.Check
                                  key={idx}
                                  type="checkbox"
                                  label={option}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const newValues: string[] = e.target.checked
                                      ? [...selectedValues, option]
                                      : selectedValues.filter((v: string) => v !== option);
                                    handleCustomFieldChange(field.id, newValues);
                                  }}
                                  disabled={isBlocked}
                                />
                              );
                            })}
                          </div>
                        );
                      } else {
                        const stringValue = fieldValue !== undefined && fieldValue !== null 
                          ? String(fieldValue) 
                          : '';
                        return (
                          <Form.Select
                            value={stringValue}
                            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                          >
                            <option value="">Selecione...</option>
                            {options.map((option: string, idx: number) => (
                              <option key={idx} value={option}>
                                {option}
                              </option>
                            ))}
                          </Form.Select>
                        );
                      }
                    })()
                  ) : field.field_type === 'date' ? (
                    <Form.Control
                      type="date"
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                    />
                  ) : field.field_type === 'period' ? (
                    (() => {
                      let periodValue = { start: '', end: '' };
                      if (fieldValue) {
                        try {
                          periodValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                        } catch {
                          periodValue = { start: fieldValue || '', end: '' };
                        }
                      }
                      return (
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="date"
                            placeholder="Data Início"
                            value={periodValue.start || ''}
                            onChange={(e) => {
                              const newPeriod = { ...periodValue, start: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                          />
                          <Form.Control
                            type="date"
                            placeholder="Data Fim"
                            value={periodValue.end || ''}
                            onChange={(e) => {
                              const newPeriod = { ...periodValue, end: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newPeriod));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                          />
                        </div>
                      );
                    })()
                  ) : field.field_type === 'link' ? (
                    <Form.Control
                      type="url"
                      value={fieldValue || ''}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="https://example.com"
                    />
                  ) : field.field_type === 'user' ? (
                    (() => {
                      const filteredUserList = userSearchTerm
                        ? users.filter(u => 
                            u.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                            u.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
                          ).slice(0, 10)
                        : [];
                      const selectedUser = users.find(u => u.id === parseInt(fieldValue || '0'));
                      const isInvalidUserInput = !!userSearchTerm.trim() && !selectedUser;
                      
                      return (
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            value={selectedUser ? `${selectedUser.name} (${selectedUser.email})` : userSearchTerm}
                            onChange={(e) => {
                              setSearchUsers({ ...searchUsers, [field.id]: e.target.value });
                              if (!e.target.value) {
                                handleCustomFieldChange(field.id, '');
                              }
                            }}
                            onFocus={() => {
                              if (!userSearchTerm) {
                                setFilteredUsers({ ...filteredUsers, [field.id]: users.slice(0, 10) });
                              }
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            isInvalid={isInvalidUserInput}
                            placeholder="Digite o nome ou email do usuário"
                          />
                          {userSearchTerm && filteredUserList.length > 0 && !selectedUser && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              backgroundColor: 'white',
                              border: '1px solid #ced4da',
                              borderRadius: '0 0 0.375rem 0.375rem',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}>
                              {filteredUserList.map((user) => (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    handleCustomFieldChange(field.id, user.id.toString());
                                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #e9ecef',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  {user.name} ({user.email})
                                </div>
                              ))}
                            </div>
                          )}
                          {isInvalidUserInput && (
                            <Form.Text className="text-danger d-block mt-1">
                              Selecione um usuário cadastrado na lista.
                            </Form.Text>
                          )}
                        </div>
                      );
                    })()
                  ) : (field.field_type === 'approver' || field.field_type === 'contract_manager_approver') ? (
                    (() => {
                      const approverSearchTerm = searchUsers[field.id] || '';
                      const approverUsers = users.filter(
                        (u) =>
                          u.is_active &&
                          (field.field_type === 'contract_manager_approver' || !!u.can_approve)
                      );
                      const filteredApproverList = approverSearchTerm
                        ? approverUsers.filter(u => 
                            u.name?.toLowerCase().includes(approverSearchTerm.toLowerCase()) ||
                            u.email?.toLowerCase().includes(approverSearchTerm.toLowerCase())
                          ).slice(0, 10)
                        : [];
                      const selectedApprover = approverUsers.find(u => u.id === parseInt(fieldValue || '0'));
                      
                      return (
                        <div style={{ position: 'relative' }}>
                          <Form.Control
                            type="text"
                            value={selectedApprover ? `${selectedApprover.name} (${selectedApprover.email})` : approverSearchTerm}
                            onChange={(e) => {
                              setSearchUsers({ ...searchUsers, [field.id]: e.target.value });
                              if (!e.target.value) {
                                handleCustomFieldChange(field.id, '');
                              }
                            }}
                            onFocus={() => {
                              if (!approverSearchTerm) {
                                setFilteredUsers({ ...filteredUsers, [field.id]: approverUsers.slice(0, 10) });
                              }
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            placeholder="Digite o nome ou email do aprovador"
                          />
                          {approverSearchTerm && filteredApproverList.length > 0 && !selectedApprover && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              backgroundColor: 'white',
                              border: '1px solid #ced4da',
                              borderRadius: '0 0 0.375rem 0.375rem',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            }}>
                              {filteredApproverList.map((user) => (
                                <div
                                  key={user.id}
                                  onClick={() => {
                                    handleCustomFieldChange(field.id, user.id.toString());
                                    setSearchUsers({ ...searchUsers, [field.id]: '' });
                                    setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #e9ecef',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'white';
                                  }}
                                >
                                  {user.name} ({user.email})
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'analyst' ? (
                    (() => {
                      const analystSearchTerm = searchUsers[field.id] || '';
                      const filteredAnalystList = analystSearchTerm
                        ? analystUsers.filter(u => 
                            u.name?.toLowerCase().includes(analystSearchTerm.toLowerCase()) ||
                            u.email?.toLowerCase().includes(analystSearchTerm.toLowerCase())
                          ).slice(0, 10)
                        : [];
                      const selectedAnalyst = analystUsers.find(u => u.id === parseInt(fieldValue || '0'));
                      
                      return (
                        <div style={{ position: 'relative' }}>
                          {groupId ? (
                            <>
                              <Form.Control
                                type="text"
                                value={selectedAnalyst ? `${selectedAnalyst.name} (${selectedAnalyst.email})` : analystSearchTerm}
                                onChange={(e) => {
                                  setSearchUsers({ ...searchUsers, [field.id]: e.target.value });
                                  if (!e.target.value) {
                                    handleCustomFieldChange(field.id, '');
                                  }
                                }}
                                onFocus={() => {
                                  if (!analystSearchTerm) {
                                    setFilteredUsers({ ...filteredUsers, [field.id]: analystUsers.slice(0, 10) });
                                  }
                                }}
                                required={isRequired && !isBlocked}
                                disabled={isBlocked}
                                readOnly={isBlocked}
                                placeholder="Digite o nome ou email do analista"
                              />
                              {analystSearchTerm && filteredAnalystList.length > 0 && !selectedAnalyst && (
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  zIndex: 1000,
                                  backgroundColor: 'white',
                                  border: '1px solid #ced4da',
                                  borderRadius: '0 0 0.375rem 0.375rem',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                }}>
                                  {filteredAnalystList.map((analyst) => (
                                    <div
                                      key={analyst.id}
                                      onClick={() => {
                                        handleCustomFieldChange(field.id, analyst.id.toString());
                                        setSearchUsers({ ...searchUsers, [field.id]: '' });
                                        setFilteredUsers({ ...filteredUsers, [field.id]: [] });
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid #e9ecef',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                      }}
                                    >
                                      {analyst.name} ({analyst.email})
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-muted">
                              <small>Selecione um grupo primeiro</small>
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'integer' ? (
                    <Form.Control
                      type="number"
                      step="1"
                      min="0"
                      value={fieldValue || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Apenas inteiros não negativos
                        if (value === '' || /^\d+$/.test(value)) {
                          handleCustomFieldChange(field.id, value);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="0"
                    />
                  ) : field.field_type === 'decimal' ? (
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={fieldValue || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || (parseFloat(v) >= 0 && !/^-/.test(v))) {
                          handleCustomFieldChange(field.id, v);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      required={isRequired && !isBlocked}
                      disabled={isBlocked}
                      readOnly={isBlocked}
                      placeholder="0.00"
                    />
                  ) : field.field_type === 'currency' ? (
                    (() => {
                      let currencyValue = { currency: 'BRL', value: '' };
                      if (fieldValue) {
                        try {
                          currencyValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
                        } catch {
                          currencyValue = { currency: 'BRL', value: fieldValue || '' };
                        }
                      }
                      return (
                        <div className="d-flex gap-2 align-items-center">
                          <Form.Select
                            value={currencyValue.currency || 'BRL'}
                            onChange={(e) => {
                              const newCurrency = { ...currencyValue, currency: e.target.value };
                              handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
                            }}
                            disabled={isBlocked}
                            className="csp-wave1-width-150"
                          >
                            <option value="BRL">BRL (R$)</option>
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                          </Form.Select>
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={formatCurrencyInputDisplay(
                              currencyValue.value || '',
                              currencyValue.currency || 'BRL'
                            )}
                            onChange={(e) => {
                              const normalized = normalizeCurrencyInput(e.target.value);
                              if (normalized === null) return;
                              const newCurrency = { ...currencyValue, value: normalized };
                              handleCustomFieldChange(field.id, JSON.stringify(newCurrency));
                            }}
                            required={isRequired && !isBlocked}
                            disabled={isBlocked}
                            readOnly={isBlocked}
                            placeholder="Ex.: 30000,01"
                          />
                          {currencyValue.value && (
                            <Form.Text className="text-muted text-nowrap">
                              {formatCurrency(currencyValue.value, currencyValue.currency)}
                            </Form.Text>
                          )}
                        </div>
                      );
                    })()
                  ) : field.field_type === 'table' ? (
                    <CustomTableField
                      value={fieldValue || createDefaultTableValue()}
                      onChange={(nextValue) => handleCustomFieldChange(field.id, nextValue)}
                      disabled={isBlocked}
                    />
                  ) : field.field_type === 'file' ? (
                    <div>
                      <Form.Control
                        type="file"
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          const file = target.files?.[0] || null;
                          if (file) {
                            const error = validateAttachmentFile(file);
                            if (error) {
                              showAlert({
                                title: 'Arquivo inválido',
                                message: error,
                                variant: 'warning',
                              });
                              target.value = '';
                              setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                              return;
                            }
                          }
                          setCustomFieldFiles({ ...customFieldFiles, [field.id]: file });
                        }}
                        disabled={isBlocked}
                        accept={ATTACHMENT_ACCEPT}
                      />
                      {customFieldFiles[field.id] && (
                        <small className="text-muted d-block mt-1">
                          Arquivo selecionado: {(customFieldFiles[field.id] as File).name}
                        </small>
                      )}
                    </div>
                  ) : field.field_type === 'files' ? (
                    <div>
                      <Form.Control
                        type="file"
                        multiple
                        onChange={(e) => {
                          const target = e.target as HTMLInputElement;
                          const files = target.files ? Array.from(target.files) : null;
                          if (files && files.length > 0) {
                            const error = validateAttachmentFiles(files);
                            if (error) {
                              showAlert({
                                title: 'Arquivo inválido',
                                message: error,
                                variant: 'warning',
                              });
                              target.value = '';
                              setCustomFieldFiles({ ...customFieldFiles, [field.id]: null });
                              return;
                            }
                          }
                          setCustomFieldFiles({ ...customFieldFiles, [field.id]: files });
                        }}
                        disabled={isBlocked}
                        accept={ATTACHMENT_ACCEPT}
                      />
                      {customFieldFiles[field.id] && Array.isArray(customFieldFiles[field.id]) && (
                        <small className="text-muted d-block mt-1">
                          {(customFieldFiles[field.id] as File[]).length} arquivo(s) selecionado(s)
                        </small>
                      )}
                    </div>
                  ) : field.field_type === 'check' ? (
                    (() => {
                      const options: string[] = field.options
                        ? (() => {
                            try {
                              const parsed = JSON.parse(field.options);
                              if (Array.isArray(parsed)) return parsed.map((opt) => String(opt).trim()).filter((opt) => opt.length > 0);
                              if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') return [String(parsed).trim()];
                            } catch {
                              return field.options.split(/[,\n]/).map((opt: string) => opt.trim()).filter((opt: string) => opt.length > 0);
                            }
                            return [];
                          })()
                        : [];
                      const hasOptions = options.length > 0;

                      if (hasOptions) {
                        let selectedValues: string[] = [];
                        if (fieldValue) {
                          try {
                            if (typeof fieldValue === 'string') {
                              const parsed = JSON.parse(fieldValue);
                              if (Array.isArray(parsed)) {
                                selectedValues = parsed.map((v) => String(v));
                              } else if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
                                selectedValues = [String(parsed)];
                              }
                            } else if (Array.isArray(fieldValue)) {
                              selectedValues = fieldValue.map((v) => String(v));
                            } else {
                              selectedValues = [String(fieldValue)];
                            }
                          } catch {
                            selectedValues = String(fieldValue).trim() ? [String(fieldValue)] : [];
                          }
                        }

                        const selectedValue = selectedValues[0] || '';
                        const shouldShowInfoText = field.checkbox_text_trigger_option
                          ? (field.allow_multiple
                              ? selectedValues.includes(field.checkbox_text_trigger_option)
                              : selectedValue === field.checkbox_text_trigger_option)
                          : (field.allow_multiple ? selectedValues.length > 0 : !!selectedValue);

                        return (
                          <div>
                            {field.checkbox_link && (
                              <div className="mb-2">
                                <a
                                  href={field.checkbox_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  {field.checkbox_link_text || 'Ler Diretrizes'}
                                </a>
                              </div>
                            )}
                            <div>
                              {options.map((option: string, idx: number) => (
                                <Form.Check
                                  key={idx}
                                  className="custom-field-check"
                                  type={field.allow_multiple ? 'checkbox' : 'radio'}
                                  name={field.allow_multiple ? undefined : `field-${field.id}`}
                                  label={option}
                                  checked={field.allow_multiple ? selectedValues.includes(option) : selectedValue === option}
                                  onChange={(e) => {
                                    if (field.allow_multiple) {
                                      const newValues: string[] = e.target.checked
                                        ? [...selectedValues, option]
                                        : selectedValues.filter((v: string) => v !== option);
                                      handleCustomFieldChange(field.id, newValues);
                                      return;
                                    }
                                    handleCustomFieldChange(field.id, e.target.checked ? option : '');
                                  }}
                                  disabled={isBlocked}
                                />
                              ))}
                            </div>
                            {field.checkbox_text && shouldShowInfoText && (
                              <div className="alert alert-info mt-2">
                                {field.checkbox_text}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Checkbox único (comportamento original)
                        const isChecked = fieldValue === 'true' || fieldValue === true || fieldValue === 1 || fieldValue === '1';
                        return (
                          <div>
                            {field.checkbox_link && (
                              <div className="mb-2">
                                <a
                                  href={field.checkbox_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  {field.checkbox_link_text || 'Ler Diretrizes'}
                                </a>
                              </div>
                            )}
                            <Form.Check
                              className="custom-field-check"
                              type="checkbox"
                              label={field.checkbox_label || field.label}
                              checked={isChecked}
                              onChange={(e) => {
                                handleCustomFieldChange(field.id, e.target.checked ? 'true' : 'false');
                              }}
                              disabled={isBlocked}
                            />
                            {isChecked && field.checkbox_text && (
                              <div className="alert alert-info mt-2">
                                {field.checkbox_text}
                              </div>
                            )}
                          </div>
                        );
                    })()
                  ) : null}
                </Form.Group>
              );
            };

            const customTabs = Object.entries(groupedFields).map(([groupName, fields]) => {
              const visibleFields = fields.filter(shouldShowField);
              return (
                <Tab key={groupName} eventKey={groupName} title={groupName}>
                  <div className="pt-3">
                    {visibleFields.length > 0 ? (
                      visibleFields.map(renderField)
                    ) : (
                      <div className="text-muted mb-3">
                        <small>Preencha os campos dependentes na aba Informações para liberar os campos desta aba.</small>
                      </div>
                    )}
                  </div>
                </Tab>
              );
            });

            if (hasUngrouped) {
              customTabs.push(
                <Tab key="geral" eventKey="geral" title={hasGroups ? 'Geral' : 'Campos Customizados'}>
                  <div className="pt-3">
                    {(() => {
                      const visibleUngroupedFields = ungroupedFields.filter(shouldShowField);
                      if (visibleUngroupedFields.length > 0) {
                        return visibleUngroupedFields.map(renderField);
                      }
                      return (
                        <div className="text-muted mb-3">
                          <small>Preencha os campos dependentes na aba Informações para liberar os campos desta aba.</small>
                        </div>
                      );
                    })()}
                  </div>
                </Tab>
              );
            }

            return customTabs;
          })() : null}
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap gap-2">
          <div className="me-auto d-flex flex-wrap align-items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline-primary"
              onClick={() => {
                setTemplateSearchTerm('');
                setTemplateFilterCategory('');
                setShowTemplatesModal(true);
              }}
            >
              Meus templates
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline-success"
              onClick={() => setShowSaveTemplateModal(true)}
              disabled={loading || savingTemplate}
            >
              Salvar como template
            </Button>
          </div>
          <Button type="button" variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Ticket'}
          </Button>
        </Modal.Footer>
      </Form>
      </Modal>
      <Modal
        show={showTemplatesModal}
        onHide={() => setShowTemplatesModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Meus templates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              placeholder="Buscar templates"
              value={templateSearchTerm}
              onChange={(e) => setTemplateSearchTerm(e.target.value)}
            />
            <Form.Select
              value={templateFilterCategory}
              onChange={(e) => setTemplateFilterCategory(e.target.value)}
              className="csp-wave1-max-width-220"
            >
              <option value="">Todas categorias</option>
              {templateCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Form.Select>
          </div>

          {loadingTemplates ? (
            <div className="text-muted">Carregando templates...</div>
          ) : filteredTemplateOptions.length === 0 ? (
            <div className="text-muted">Nenhum template encontrado.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm align-middle">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Categoria</th>
                    <th>Descrição</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplateOptions.map((template) => (
                    <tr key={template.id}>
                      <td>{template.title}</td>
                      <td>{template.category || <span className="text-muted">-</span>}</td>
                      <td className="csp-wave1-pre-wrap">{template.description}</td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              applyTemplate(template);
                              setShowTemplatesModal(false);
                            }}
                          >
                            Usar
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDeleteTemplate(template.id)}
                            disabled={deletingTemplateId === template.id}
                          >
                            {deletingTemplateId === template.id ? 'Excluindo...' : 'Excluir'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setShowTemplatesModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSaveTemplateModal}
        onHide={() => setShowSaveTemplateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Salvar template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-0">
            <Form.Label>Categoria (opcional)</Form.Label>
            <Form.Control
              placeholder="Ex.: Impressora, Acesso, Rede..."
              value={templateCategory}
              onChange={(e) => setTemplateCategory(e.target.value)}
              list="ticket-template-categories"
            />
            <datalist id="ticket-template-categories">
              {templateCategories.map((item) => (
                <option key={item} value={item} />
              ))}
            </datalist>
            <Form.Text className="text-muted">
              O template salva título, descrição e demais campos da abertura.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setShowSaveTemplateModal(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="success"
            onClick={handleSaveTemplate}
            disabled={savingTemplate}
          >
            {savingTemplate ? 'Salvando...' : 'Salvar template'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Tickets;

